import { useCurrentUser } from '@guryou/redux';
import { deleteMessage, downloadFromFirebase, getFileNameFromUrl } from 'firebase/firebaseFunctions';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DocIcon, DownloadIcon, MoreIcon } from '@guryou/html-components';
import { Icon } from '../../components/shared';

const FilePlayer = ({ message, isMine }) => {
  const download = async docUrl => {
    downloadFromFirebase(docUrl);
  };

  const getFileIcon = fileName => {
    const extension = fileName
      .split('.')
      .pop()
      ?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return (
          <div class="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case 'doc':
      case 'docx':
        return (
          <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case 'xls':
      case 'xlsx':
        return (
          <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case 'zip':
      case 'rar':
        return (
          <div class="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      default:
        return (
          <div class={`w-10 h-10 rounded-lg flex items-center justify-center ${isMine ? 'bg-white/20' : 'bg-gray-100'}`}>
            <DocIcon fontSize="large" className={isMine ? 'text-white' : 'text-gray-600'} />
          </div>
        );
    }
  };

  const getFileSize = url => {
    // This would need to be implemented with actual file size data
    return '2.5 MB';
  };

  const fileName = message.id.includes('temp') ? message.content : getFileNameFromUrl(message.content);

  return (
    <div class="flex items-center gap-3 my-2 relative">
      <div class={`flex items-center gap-3 flex-1 p-3 rounded-lg transition-colors hover:opacity-90 ${isMine ? 'bg-white/20' : 'bg-gray-100/50'}`}>
        {/* File Icon */}
        {getFileIcon(fileName)}

        {/* File Info */}
        <div class="flex-1 min-w-0">
          <div class={`text-sm font-medium truncate ${isMine ? 'text-white' : 'text-gray-900'}`}>{fileName}</div>
          <div class={`text-xs ${isMine ? 'text-white/70' : 'text-gray-500'}`}>{getFileSize(message.content)}</div>
        </div>

        {/* Download Button */}
        {!message.id.includes('temp') && (
          <button
            onClick={() => download(message.content)}
            class={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
              isMine ? 'bg-white/30 hover:bg-white/40 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <DownloadIcon />
          </button>
        )}
      </div>

      {/* Loading Overlay */}
      {message?.id?.includes('temp') && (
        <div class="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-10">
          <div class="w-full p-6 space-y-4">
            {/* Progress Bar */}
            <div class="w-full bg-gray-400 rounded-full h-2">
              <div
                class="bg-[var(--feel-primary)] h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${message.uploadProgress || 0}%` }}
              ></div>
            </div>
            {/* Percentage */}
            <div class="text-center">
              <span class="text-sm font-medium text-white">{message.uploadProgress || 0}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AudioPlayer = ({ message, isMine }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [waveformData, setWaveformData] = useState([]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateDuration = () => {
      setDuration(audio.duration);
      setIsLoading(false);
      // Generate waveform data based on duration
      generateWaveformData(audio.duration);
    };

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const generateWaveformData = duration => {
    // Generate random waveform bars for visual representation
    const bars = Array.from({ length: 50 }, () => Math.random() * 20 + 4);
    setWaveformData(bars);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const formatTime = time => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div class="flex items-center gap-3 my-2 relative">
      <div class={`flex items-center gap-3 flex-1 p-3 rounded-lg ${isMine ? 'bg-white/20' : 'bg-gray-100/50'}`}>
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          disabled={isLoading}
          class={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
            isMine ? 'bg-white/30 hover:bg-white/40 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
          }`}
        >
          {isLoading ? (
            <div class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          ) : isPlaying ? (
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg class="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </button>

        {/* Waveform Visualization */}
        <div class="flex-1 flex items-center">
          <div class={`flex-1 h-6 flex items-center gap-0.5 cursor-pointer`} onClick={togglePlay}>
            {waveformData.map((height, i) => {
              const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
              const barProgress = (i / waveformData.length) * 100;
              const isPlayed = barProgress <= progress;

              return (
                <div
                  key={i}
                  class={`rounded-sm transition-colors ${
                    isPlayed ? (isMine ? 'bg-white' : 'bg-[var(--feel-primary)]') : isMine ? 'bg-white/30' : 'bg-gray-400'
                  }`}
                  style={{
                    width: '2px',
                    height: `${height}px`,
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Duration */}
        <span class={`text-xs font-medium ${isMine ? 'text-white/80' : 'text-gray-600'}`}>{formatTime(duration)}</span>

        {/* Hidden Audio Element */}
        <audio ref={audioRef} src={message?.content} preload="metadata" />
      </div>

      {/* Loading Overlay */}
      {message?.id?.includes('temp') && (
        <div class="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-10">
          <div class="w-full p-6 space-y-4">
            {/* Progress Bar */}
            <div class="w-full bg-gray-400 rounded-full h-2">
              <div
                class="bg-[var(--feel-primary)] h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${message.uploadProgress || 0}%` }}
              ></div>
            </div>
            {/* Percentage */}
            <div class="text-center">
              <span class="text-sm font-medium text-white">{message.uploadProgress || 0}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ChatBubble = props => {
  const { t } = useTranslation();
  const { username } = useSelector(state => state.auth.data);
  const { user } = useCurrentUser();
  const [isOpenDropdown, setIsOpenDropdown] = React.useState(false);

  const dropdownRef = React.useRef(null);

  const { isMine, message, currentChat } = props;

  const processMessageContent = (content, isMine) => {
    return content.replace(/<a\s+[^>]*style="[^"]*color:\s*(?:#E6616A|var\(--feel-primary\))[^"]*"[^>]*>/g, match => {
      if (isMine) {
        return match.replace(/color:\s*#E6616A|color:\s*var\(--feel-primary\)/g, 'color: white');
      } else {
        return match;
      }
    });
  };

  function handleOpenOrCloseDropdown() {
    setIsOpenDropdown(!isOpenDropdown);
  }

  async function handleDeleteMessage() {
    try {
      await deleteMessage(currentChat.id, message.id);
    } catch (error) {}
  }

  const functionRenderBubble = () => {
    if (message.type === 'audio') {
      return <AudioPlayer message={message} isMine={isMine} />;
    }

    if (message.type === 'file') {
      return <FilePlayer message={message} isMine={isMine} />;
    }

    if (message.type === 'image') {
      const downloadFromFirebase = async imageUrl => {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();

          const url = window.URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = `${getFileNameFromUrl(imageUrl)}.jpg`;
          document.body.appendChild(link);
          link.click();

          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {}
      };

      return (
        <div class="group relative my-2.5">
          <div
            class={`absolute w-full h-full ${message.id.includes('temp') ? 'bg-black/50' : 'bg-gray-900/50'} opacity-0 ${
              message.id.includes('temp') ? 'opacity-100' : 'group-hover:opacity-100'
            } transition-opacity duration-300 rounded-lg flex items-center justify-center`}
          >
            {message.id.includes('temp') ? (
              <div class="flex flex-col items-center gap-4">
                {/* Progress Bar */}
                <div class="w-32 bg-gray-400 rounded-full h-2">
                  <div
                    class="bg-[var(--feel-primary)] h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${message.uploadProgress || 0}%` }}
                  ></div>
                </div>
                {/* Percentage */}
                <span class="text-sm font-medium text-white">{message.uploadProgress || 0}%</span>
              </div>
            ) : (
              <button
                onClick={() => downloadFromFirebase(message.content)}
                data-tooltip-target="download-image"
                class="inline-flex items-center justify-center rounded-full h-10 w-10 bg-white/30 hover:bg-white/50 focus:ring-4 focus:outline-none dark:text-white focus:ring-gray-50"
              >
                <DownloadIcon />
              </button>
            )}
            <div
              id="download-image"
              role="tooltip"
              class="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip dark:bg-gray-700"
            >
              {t('CHAT.downloadImage')}
              <div class="tooltip-arrow" data-popper-arrow></div>
            </div>
          </div>
          <img src={message.content} class="rounded-lg" alt="" />
        </div>
      );
    }
    return <div className="mt-2" dangerouslySetInnerHTML={{ __html: processMessageContent(message.content, isMine) }} />;
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpenDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div class={`flex ${isMine ? 'justify-end' : 'justify-start'} gap-2.5 mb-4`}>
      {!isMine && <img class="w-8 h-8 rounded-full ring-2 ring-gray-200" src={currentChat?.user?.image || message?.user?.image} alt="" />}
      <div class={`flex flex-col gap-1 w-full max-w-[320px] group`}>
        <div class={`flex items-center space-x-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
          <span class="text-sm font-semibold text-gray-900">{isMine ? username : currentChat?.name}</span>
          <span class="text-xs font-normal text-gray-500">
            {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        <div class={`relative flex items-center ${isMine ? 'justify-end' : 'justify-start'} gap-2`}>
          {isMine && (
            <div className="relative flex items-center">
              <button
                id="dropdownMenuIconButton"
                data-dropdown-toggle="dropdownDots"
                data-dropdown-placement="bottom-start"
                class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                type="button"
                onClick={handleOpenOrCloseDropdown}
              >
                <MoreIcon style={{ fontSize: '1.1rem' }} className="text-gray-900" />
              </button>
              {isOpenDropdown && (
                <div
                  id="dropdownDots"
                  class="z-10 bg-white divide-y divide-gray-100 rounded-lg shadow-lg w-32 absolute right-0 top-8 border border-gray-200"
                  ref={dropdownRef}
                >
                  <ul class="py-1 text-sm text-gray-700" aria-labelledby="dropdownMenuIconButton">
                    <li>
                      <button
                        class="flex items-center px-4 py-2 hover:bg-gray-100 w-full text-left text-gray-700 hover:text-gray-900"
                        onClick={handleDeleteMessage}
                      >
                        <Icon name="trash" className="mr-2" size="sm" />
                        {t('CHAT.deleteMessage')}
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
          <div
            class={`flex flex-col leading-1.5 p-4 border-gray-200 ${
              isMine ? 'bg-[var(--feel-primary)] rounded-s-xl rounded-ee-xl' : 'bg-gray-100 rounded-e-xl rounded-es-xl'
            }`}
          >
            <div class={isMine ? 'text-white' : 'text-gray-900'}>{functionRenderBubble()}</div>
          </div>
        </div>
      </div>
      {isMine && <img class="w-8 h-8 rounded-full ring-2 ring-[var(--feel-primary)]" src={user?.image} alt="" />}
    </div>
  );
};

export default ChatBubble;
